import { useState } from 'react';
import { Car, CheckCircle2, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);

export default function TradeInPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    year: '', make: '', model: '', trim: '', mileage: '', condition: 'good', vin: '',
    exterior_damage: false, mechanical_issues: false, accident_history: false,
    customer_name: '', customer_email: '', customer_phone: '', postal_code: '',
  });
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.year || !form.make || !form.model || !form.mileage || !form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error(t('carvana.tradein.errFill', 'Merci de remplir tous les champs requis'));
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, year: Number(form.year), mileage: Number(form.mileage) };
      const res = await axios.post(`${API}/tradein/instant-offer`, payload);
      setOffer(res.data.estimate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1">
        <section className="bg-gradient-to-br from-[#0c2340] via-[#0c2340] to-[#3b82f6] text-white py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="inline-block bg-emerald-500/20 border border-emerald-400 text-emerald-100 text-xs uppercase tracking-widest px-3 py-1 rounded-full font-bold mb-4">
              {t('carvana.tradein.badge', 'Offre instantanée')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4">
              {t('carvana.tradein.title', 'Obtenez la valeur de votre véhicule en 60 secondes')}
            </h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              {t('carvana.tradein.subtitle', 'Estimation instantanée basée sur le marché québécois. Sans engagement.')}
            </p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          {!offer ? (
            <form onSubmit={submit} className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-5" data-testid="tradein-form">
              <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2"><Car className="h-5 w-5 text-[#3b82f6]" />{t('carvana.tradein.vehicle', 'Votre véhicule')}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <input required placeholder={t('carvana.tradein.year', 'Année')} type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-year" />
                <input required placeholder={t('carvana.tradein.make', 'Marque')} value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-make" />
                <input required placeholder={t('carvana.tradein.model', 'Modèle')} value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-model" />
                <input placeholder={t('carvana.tradein.trim', 'Version')} value={form.trim} onChange={(e) => setForm({ ...form, trim: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input required placeholder={t('carvana.tradein.mileage', 'Kilométrage')} type="number" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-mileage" />
                <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-condition">
                  <option value="excellent">{t('carvana.tradein.condExcellent', 'Excellente')}</option>
                  <option value="good">{t('carvana.tradein.condGood', 'Bonne')}</option>
                  <option value="fair">{t('carvana.tradein.condFair', 'Passable')}</option>
                  <option value="poor">{t('carvana.tradein.condPoor', 'Mauvaise')}</option>
                </select>
                <input placeholder="VIN" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm font-mono" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer">
                  <input type="checkbox" checked={form.exterior_damage} onChange={(e) => setForm({ ...form, exterior_damage: e.target.checked })} />
                  {t('carvana.tradein.exteriorDamage', 'Dommages extérieurs')}
                </label>
                <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer">
                  <input type="checkbox" checked={form.mechanical_issues} onChange={(e) => setForm({ ...form, mechanical_issues: e.target.checked })} />
                  {t('carvana.tradein.mechanicalIssues', 'Problèmes mécaniques')}
                </label>
                <label className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg cursor-pointer">
                  <input type="checkbox" checked={form.accident_history} onChange={(e) => setForm({ ...form, accident_history: e.target.checked })} />
                  {t('carvana.tradein.accidentHistory', 'Historique d\'accident')}
                </label>
              </div>

              <h2 className="text-xl font-bold text-slate-900 pt-4 border-t border-slate-100">{t('carvana.tradein.you', 'Vos coordonnées')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input required placeholder={t('carvana.tradein.name', 'Nom complet')} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-name" />
                <input required type="email" placeholder={t('carvana.tradein.email', 'Courriel')} value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" data-testid="tradein-email" />
                <input required type="tel" placeholder={t('carvana.tradein.phone', 'Téléphone')} value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
                <input placeholder={t('carvana.tradein.postal', 'Code postal')} value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value.toUpperCase() })} className="border border-slate-300 rounded-lg px-3 py-2.5 text-sm" />
              </div>

              <button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-xl text-lg disabled:opacity-50" data-testid="tradein-submit-btn">
                {loading ? t('carvana.tradein.loading', 'Calcul...') : t('carvana.tradein.getOffer', 'Obtenir mon offre')}
              </button>
            </form>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="tradein-offer-result">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                <DollarSign className="h-10 w-10 text-emerald-600" />
              </div>
              <p className="text-sm uppercase text-slate-500 tracking-wider mb-2">{t('carvana.tradein.yourOffer', 'Votre offre instantanée')}</p>
              <p className="text-5xl font-black text-emerald-600 mb-2">{fmt(offer.estimate_low_cad)} — {fmt(offer.estimate_high_cad)}</p>
              <p className="text-slate-600 mb-6">{t('carvana.tradein.estMid', 'Estimation moyenne')}: <span className="font-bold">{fmt(offer.estimate_mid_cad)}</span></p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700 flex items-start gap-2 mb-6 text-left">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <p>{t('carvana.tradein.next', 'Nous vous contacterons dans les 24 heures pour confirmer l\'offre finale après une inspection virtuelle rapide.')}</p>
              </div>
              <button onClick={() => setOffer(null)} className="text-[#3b82f6] font-semibold" data-testid="tradein-restart">
                {t('carvana.tradein.newQuote', 'Nouvelle évaluation')}
              </button>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
